import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router";
import Select from "react-select";

import { VStack, HStack, Error, Box } from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import {
  useAddEsopDetails,
  useUpdateEsopDetails,
} from "../../queries/shareholders";
import { useInternalSwitchStateStore } from "../../store/searchDropDown";
import SearchAutoCompleteDropDown from "../../components/shared/SearchAutoCompleteDropDown";
import { SwitchDropDownModel } from "../rounds/RoundsAllTransactions";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { ShowNumberInWords } from "../../components/shared/UiElement";
import { convertToNumber, filterOnlyNumbers } from "../../utils/numUtils";
import { dropdownCustomStyles } from "../../components/shared/MultiDropDownSelect";

type StakeholderDetailsModel = {
  shareholderId: string;
  reservedPool: number | string;
  conversionRatio: number;
  noOfShares: number | string;
  notes: string;
  allotmentDate?: string;
};

type StakeholderDetailsPopupProps = {
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
  data?: any;
  mode: "Add" | "Update";
  shareholderId: string;
  shareholderName?: string;
  existingStakeholders?: any[];
};

export default function StakeholderDetailsPopup({
  onPrimaryAction,
  onSecondaryAction,
  data,
  mode,
  shareholderId,
  shareholderName,
  existingStakeholders,
}: StakeholderDetailsPopupProps) {
  const { id } = useParams();
  const currencyType = getCurrencyType();
  const { mutate: addEsopDetail } = useAddEsopDetails();

  const { mutate: updateEsopDetail } = useUpdateEsopDetails();
  const transactionSwitchStore = useInternalSwitchStateStore();

  const [onHoverReservedPool, setOnHoverReservedPool] = useState(false);
  const [onHoverNoOfShares, setOnHoverNoOfShares] = useState(false);
  const [selectedShareholder, setSelectedShareholder] =
    useState<SwitchDropDownModel>(
      data?.shareholderId
        ? {
            name: data.shareholderName || "",
            id: data.shareholderId,
            type: "",
          }
        : shareholderName && shareholderId
        ? {
            name: shareholderName,
            id: shareholderId,
            type: "",
          }
        : { name: "", id: "", type: "" }
    );

  const stakeholderInitialValues: StakeholderDetailsModel = {
    shareholderId: data?.shareholderId || "",
    reservedPool: data?.reservedPool ?? "",
    conversionRatio: data?.conversionRatio || 1,
    noOfShares: data?.noOfShares ?? "",
    notes: data?.notes ?? "",
    allotmentDate: data?.allotmentDate || "",
  };

  const filteredShareholder = transactionSwitchStore.shareholders.filter(
    (shareholder: any) => shareholder.type.toLowerCase() !== "options"
  );

  const [shareHolderOptions, setShareholderOptions] = useState<
    { label: string; value: string }[]
  >(
    filteredShareholder.map((shareholder: any) => ({
      label: shareholder.name,
      value: shareholder.id,
    }))
  );

  const [defaultShareholderValue, setDefaultShareholderValue] = useState<{
    label: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    if (mode === "Update" && data?.shareholderId) {
      const selectedOption = shareHolderOptions.find(
        (option) => option.value === data.shareholderId
      );
      if (selectedOption) {
        setDefaultShareholderValue(selectedOption);
      }
    }
  }, [mode, data?.shareholderId, shareHolderOptions]);

  useEffect(() => {
    if (existingStakeholders && existingStakeholders.length > 0) {
      const updatedShareholderList = shareHolderOptions.filter(
        (shareholder: any) =>
          !existingStakeholders.some(
            (existingShareholder: any) =>
              shareholder.value === existingShareholder.shareholderId
          )
      );

      setShareholderOptions(updatedShareholderList);
    }
  }, [existingStakeholders]);

  const stakeholderValidationSchema = Yup.object({
    shareholderId: Yup.string().required("Shareholder is required"),
    reservedPool: Yup.number()
      .min(0, "No. of Options must be greater than or equal to 0")
      .required("No. of Options is required")
      .label("No. of Options"),
    conversionRatio: Yup.number()
      .positive("Conversion Ratio must be positive")
      .required("Conversion Ratio is required")
      .label("Conversion Ratio"),
    noOfShares: Yup.number()
      .min(0, "No. of Shares must be greater than or equal to 0")
      .label("No. of Shares"),
    allotmentDate: Yup.date()
      .label("Allotment Date"),
  });

  function getSelectedValue(shareholder?: SwitchDropDownModel) {
    if (shareholder) {
      handleEventForTracking({
        eventName: "Shareholder Search for esop stakeholders",
      });
      setSelectedShareholder(shareholder);
      formik.setFieldValue("shareholderId", shareholder.id);
    }
  }
  const handleSubmit = async (values: StakeholderDetailsModel) => {
    const payload = {
      planName: data?.planName || "",
      planType: data?.planType || "ESOP",
      reservedPool: Number(values.reservedPool),
      allocatedPool: Number(values.reservedPool),
      unallocatedPool: 0,
      conversionRatio: Number(values.conversionRatio) || 1,
      shareholderId: values.shareholderId,
      notes: values?.notes || "",
      allotmentDate: values?.allotmentDate || "",
    };
    if (mode === "Add") {
      addEsopDetail(
        { planDetails: payload, cInvestorId: id || "" },
        {
          onSuccess: () => {
            toast.success("Stakeholder ESOP Details Added Successfully");
            onPrimaryAction();
          },
          onError: (err: any) => {
            toast.error(
              err?.response?.data?.errorMessage ||
                "Error adding stakeholder details"
            );
          },
        }
      );
    } else if (mode === "Update") {
      const updatedPayload = { ...payload, id: data?.id };
      updateEsopDetail(updatedPayload, {
        onSuccess: () => {
          toast.success("Stakeholder ESOP Details Updated Successfully");
          onPrimaryAction();
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.errorMessage ||
              "Error updating stakeholder details"
          );
        },
      });
    }
  };
  const formik = useFormik({
    initialValues: stakeholderInitialValues,
    enableReinitialize: mode === "Update",
    validationSchema: stakeholderValidationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const options = Number(formik.values.reservedPool) || 0;
    const ratio = Number(formik.values.conversionRatio) || 1;
    if (options > 0 && ratio > 0) {
      const calculatedShares = Math.floor(options / ratio);
      formik.setFieldValue("noOfShares", calculatedShares);
    }
  }, [formik.values.reservedPool, formik.values.conversionRatio]);

  return (
    <VStack className="w-full py-2 gap-6">
      <HStack className="justify-between px-5 border-b">
        <p className="pb-2 text-base font-medium">
          {mode} Stakeholder ESOP Details
        </p>
        <XMarkIcon
          className="w-6 h-6 cursor-pointer"
          onClick={onSecondaryAction}
        />
      </HStack>

      <VStack className="w-full gap-5 px-5">
        <div className="grid grid-cols-1 md:grid-cols-2 sm:gap-x-[8px] sm:gap-y-3 gap-x-[6px] gap-y-6">
          <div className="w-full">
            <Label className="text-sm font-normal">Shareholder</Label>
            <Select
              options={shareHolderOptions}
              styles={dropdownCustomStyles}
              className="w-full"
              classNamePrefix="select2-selection"
              value={defaultShareholderValue}
              isDisabled={mode === "Update"}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("shareholderId", selectedOption.value);
                setDefaultShareholderValue(selectedOption);
              }}
            />
            {formik.touched.shareholderId && formik.errors.shareholderId && (
              <Error text={formik.errors.shareholderId} />
            )}
          </div>

          {/* No. of Options */}
          <div className="w-full">
            <Label className="text-sm font-normal">No. of Options</Label>
            <Input
              type="text"
              onMouseEnter={() => setOnHoverReservedPool(true)}
              onMouseLeave={() => setOnHoverReservedPool(false)}
              onChange={(e: any) => {
                if (e.target.value === "") {
                  formik.setFieldValue("reservedPool", "");
                  formik.handleChange("reservedPool");
                  return;
                }
                const filteredValue = filterOnlyNumbers(e.target.value);
                const values = convertToNumber(filteredValue);
                formik.setFieldValue("reservedPool", values);
                formik.handleChange("reservedPool");
              }}
              value={
                formik.values.reservedPool?.toString() !== ""
                  ? Intl.NumberFormat(currencyType).format(
                      formik.values.reservedPool as number
                    )
                  : ""
              }
              placeholder="Enter No. of Options"
            />
            {formik.touched.reservedPool && formik.errors.reservedPool && (
              <Error text={formik.errors.reservedPool as string} />
            )}
          </div>

          {/* Conversion Ratio */}
          <div className="w-full">
            <Label className="text-sm font-normal">Conversion Ratio</Label>
            <div className="flex flex-row items-center">
              <Input
                className="w-36"
                type="number"
                {...formik.getFieldProps("conversionRatio")}
              />
              <p className="text-md pr-2 ml-2">:</p>
              <p className="text-md pr-2">1</p>
            </div>
            {formik.values.conversionRatio > 0 && (
              <p className="text-xxs font-normal text-gray-400 pt-2">
                {formik.values.conversionRatio} options will be converted to 1
                share
              </p>
            )}
            {formik.touched.conversionRatio &&
              formik.errors.conversionRatio && (
                <Error text={formik.errors.conversionRatio as string} />
              )}
          </div>

          {/* No. of Shares */}
          <div className="w-full overflow-visible">
            <HStack className="justify-between">
              <Label className="text-sm font-normal">No. of Shares</Label>
              <div className="relative group">
                <svg
                  className="w-4 h-4 text-orange-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="invisible group-hover:visible absolute z-50 px-3 py-2 text-xs text-white bg-gray-800 rounded shadow-lg -top-12 right-0 whitespace-nowrap">
                  Auto-calculated: No. of Options ÷ Conversion Ratio
                  <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 -bottom-1 right-2"></div>
                </div>
              </div>
            </HStack>
            <Input
              type="text"
              onMouseEnter={() => setOnHoverNoOfShares(true)}
              onMouseLeave={() => setOnHoverNoOfShares(false)}
              value={
                formik.values.noOfShares?.toString() !== ""
                  ? Intl.NumberFormat(currencyType).format(
                      formik.values.noOfShares as number
                    )
                  : ""
              }
              placeholder="Auto-calculated"
              disabled
              className="bg-gray-100"
            />
            {onHoverNoOfShares && (
              <ShowNumberInWords
                value={formik.values.noOfShares as number}
                width={38}
                currency={currencyType}
              />
            )}
            {formik.touched.noOfShares && formik.errors.noOfShares && (
              <Error text={formik.errors.noOfShares as string} />
            )}
          </div>
          <div className="w-full">
            <Label className="text-sm font-normal">Notes</Label>
            <textarea
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter any additional notes"
              className="w-full mt-1 p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[60px]"
            />
            {formik.touched.notes && formik.errors.notes && (
              <Error text={formik.errors.notes as string} />
            )}
          </div>
          <div className="w-full">
            <Label className="text-sm font-normal">Allotment Date</Label>
            <input
              type="date"
              name="allotmentDate"
              value={formik.values.allotmentDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full mt-1 p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formik.touched.allotmentDate && formik.errors.allotmentDate && (
              <Error text={formik.errors.allotmentDate as string} />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <HStack className="justify-between mt-6">
          <SecondaryCTAButton
            event-name="Cancel Stakeholder ESOP Modal"
            onClick={onSecondaryAction}
            type="reset"
            className="text-red-500"
          >
            Cancel
          </SecondaryCTAButton>
          <PrimaryCTAButton
            event-name="Save Stakeholder ESOP Modal"
            type="button"
            onClick={() => formik.handleSubmit()}
          >
            {mode}
          </PrimaryCTAButton>
        </HStack>
      </VStack>
    </VStack>
  );
}
